<template>
    <div class="cockpit-chart">
        <!-- TÍTULO -->
        <div class="chart-title">
            {{ title }}
        </div>

        <!-- CONTEÚDO -->
        <div ref="chartContent" class="chart-content" v-if="!noData">
            <svg ref="svgcontent"></svg>
        </div>

        <!-- FOOTER -->
        <div class="chart-footer" v-if="!noData">
            <div ref="LGbox" class="legend-chart"></div>
        </div>

        <!-- MSG NÃO HÁ DADOS -->
        <div class="chart-no-text" v-if="noData">
            <svg class="fail">
                <use :xlink:href="require('@/assets/img/icons/chart-error.svg') + '#layer'"></use>
            </svg>
            <div>{{ $t('chart-no-data') }}</div>
        </div>
    </div>
</template>

<script>
import CockpitChart from '@/components/shared/chart/CockpitChart';
// import paleta from "@/functions/color/paleta.js";
import { palette as paleta } from '@/functions/color/Palette';

export default {
    extends: CockpitChart,
    props: {
        title: {
            type: String,
            default: '',
        },
        datacontent: {
            type: Object,
            default: () => {
                return {};
            },
        },
        smoothing: {
            default: true,
        },
        painted: {
            default: true,
        },
    },
    data() {
        return {
            colorList: [],
            cores: [],
            total: 0,
            filtered: [],
            noData: false,
            dataset: [],
        };
    },
    mounted() {
        this.cores = paleta.getBarColors();

        if (!this.datacontent || this.datacontent.length <= 0) {
            this.noData = true;
        } else {
            this.generateChart();
        }
    },
    methods: {
        generateChart() {
            let dataset = this.datacontent;
            let margin = { top: 50, right: 30, bottom: 50, left: 50 },
                width = this.$refs.chartContent.offsetWidth - (margin.left + margin.right), // Use the window's width
                height = this.$refs.chartContent.offsetHeight - (margin.bottom + margin.top); // Use the window's height

            let xScale = this.$d3
                .scaleOrdinal()
                .domain(dataset.xAxis)
                .range(this.getXRange(dataset.xAxis, width));

            let yMax = this.getNextIntegerNumber(
                Math.max(...dataset.items.flatMap((item) => item.values)) || 0
            );

            let yScale = this.$d3.scaleLinear().domain([0, yMax]).range([height, 0]);

            /* Para mapear cores customizadas. Se houver uma cor definida, pega ela.
         Do contrário, pega a do array de cores. */
            let mappedColorArray = dataset.items.map((item, idx) => {
                return item.color || this.cores[idx % this.cores.length];
            });

            let color = this.$d3.scaleOrdinal(mappedColorArray);

            let line = this.$d3
                .line()
                .x(function (d, i) {
                    return xScale(i);
                })
                .y(function (d) {
                    return yScale(d);
                });

            let area = this.$d3
                .area()
                .x(function (d, i) {
                    return xScale(i);
                })
                .y0(yScale(0))
                .y1(function (d) {
                    return yScale(d);
                });

            if (this.smoothing) {
                line.curve(this.$d3.curveMonotoneX);
                area.curve(this.$d3.curveMonotoneX);
            }

            let div = this.$d3
                .select('body')
                .append('div')
                .attr('class', 'chart-tooltip')
                .style('opacity', 0);

            let svg = this.$d3
                .select(this.$refs.svgcontent)
                .attr('width', width + margin.left + margin.right)
                .attr('height', height + margin.top + margin.bottom)
                .append('g')
                .attr('transform', `translate(${margin.left || 0}, ${margin.top || 0})`);

            svg.append('g')
                .attr('class', 'x axis')
                .attr('transform', `translate(0, ${height || 0})`)
                .call(this.$d3.axisBottom(xScale));

            svg.append('g').attr('class', 'y axis').call(this.$d3.axisLeft(yScale));

            let dots = [];

            dataset.items.forEach((item) => {
                svg.append('path')
                    .datum(item.values)
                    .attr('class', 'line')
                    .attr('d', line)
                    .style('fill', '#0000')
                    .style('stroke', color(item.label))
                    .style('stroke-width', 3);

                if (this.painted === true) {
                    svg.append('path')
                        .datum(item.values)
                        .attr('class', 'area')
                        .style('fill', this.getAlfa(color(item.label), 35))
                        .attr('d', area);
                }

                item.values.forEach((v, idx) => {
                    dots.push({
                        index: idx,
                        label: item.label,
                        value: v,
                    });
                });
            });

            svg.selectAll('.dot')
                .data(dots)
                .enter()
                .append('circle')
                .attr('class', 'dot')
                .attr('cx', (dot) => xScale(dot.index))
                .attr('cy', (dot) => yScale(dot.value))
                .attr('r', 5)
                .style('stroke', (dot) => color(dot.label))
                .style('stroke-width', 3)
                .style('fill', '#FFF')
                .style('cursor', 'pointer')
                .style('pointer-events', 'all')
                .on('mouseover', (dot) => {
                    div.transition()
                        .duration(200)
                        .style('opacity', this.legendOpacity)
                        .style('color', '#FFF');

                    div.html(dot.label + ': ' + this.replaceDot(dot.value))
                        .style('left', this.$d3.event.pageX - div.node().offsetWidth / 2 + 'px')
                        .style('top', this.$d3.event.pageY - 40 + 'px');
                })
                .on('mousemove', (d) => {
                    div.style(
                        'left',
                        this.$d3.event.pageX - div.node().offsetWidth / 2 + 'px'
                    ).style('top', this.$d3.event.pageY - 40 + 'px');
                })
                .on('mouseout', (d) => {
                    div.transition().duration(500).style('opacity', 0);
                });

            let legends = this.$d3
                .select(this.$refs.LGbox)
                .attr('font-family', 'sans-serif')
                .attr('font-size', 10)
                .attr('text-anchor', 'end')
                .selectAll('.legend')
                .data(color.domain())
                .enter()
                .append('div')
                .attr('class', 'legend-box')
                .on('click', (label) => {
                    /* Se a label não existir no array de removidos... */
                    if (this.filtered.indexOf(label) == -1) {
                        /* Adicionar a label que foi retirada */
                        this.filtered.push(label);

                        /* Se todos foram fetirados, resetar */
                        if (this.filtered.length == color.domain().length) {
                            this.filtered = [];
                        }
                    } else {
                    /* Se não, remove ele */
                        this.filtered.splice(this.filtered.indexOf(label), 1);
                    }

                    yMax = this.getNextIntegerNumber(
                        Math.max(...dataset.items.flatMap((item) => item.values)) || 0
                    );

                    let filteredItems = dataset.items.filter(
                        (item) => this.filtered.indexOf(item.label) == -1
                    );

                    svg.selectAll('.line').remove();
                    svg.selectAll('.area').remove();

                    dots = [];

                    yScale = this.$d3.scaleLinear().domain([0, yMax]).range([height, 0]);

                    line = this.$d3
                        .line()
                        .x(function (d, i) {
                            return xScale(i);
                        })
                        .y(function (d) {
                            return yScale(d);
                        });

                    area = this.$d3
                        .area()
                        .x(function (d, i) {
                            return xScale(i);
                        })
                        .y0(yScale(0))
                        .y1(function (d) {
                            return yScale(d);
                        });

                    if (this.smoothing) {
                        line.curve(this.$d3.curveMonotoneX);
                        area.curve(this.$d3.curveMonotoneX);
                    }

                    svg.select('.y').transition().call(this.$d3.axisLeft(yScale)).duration(500);

                    filteredItems.forEach((item) => {
                        svg.append('path')
                            .datum(item.values)
                            .attr('class', 'line')
                            .attr('d', line)
                            .style('fill', '#0000')
                            .transition()
                            .duration(500)
                            .style('stroke', color(item.label))
                            .style('stroke-width', 3);

                        if (this.painted === true) {
                            svg.append('path')
                                .datum(item.values)
                                .attr('class', 'area')
                                .style(
                                    'fill',
                                    this.painted ? this.getAlfa(color(item.label), 35) : 'none'
                                )
                                .style('opacity', '0')
                                .attr('d', area)
                                .transition()
                                .duration(500)
                                .style('opacity', '1');
                        }

                        item.values.forEach((v, idx) => {
                            dots.push({
                                index: idx,
                                label: item.label,
                                value: v,
                            });
                        });
                    });

                    svg.selectAll('.dot').remove();

                    svg.selectAll('.dot')
                        .data(dots)
                        .enter()
                        .append('circle')
                        .attr('class', 'dot')
                        .attr('cx', (dot) => xScale(dot.index))
                        .attr('cy', (dot) => yScale(dot.value))
                        .attr('r', 5)
                        .style('stroke', (dot) => color(dot.label))
                        .style('stroke-width', 3)
                        .style('fill', '#FFF')
                        .style('cursor', 'pointer')
                        .style('pointer-events', 'all')
                        .on('mouseover', (d) => {
                            div.transition()
                                .duration(200)
                                .style('opacity', this.legendOpacity)
                                .style('color', '#FFF');

                            div.html(d.label + ': ' + this.replaceDot(d.v))
                                .style(
                                    'left',
                                    this.$d3.event.pageX - div.node().offsetWidth / 2 + 'px'
                                )
                                .style('top', this.$d3.event.pageY - 40 + 'px');
                        })
                        .on('mousemove', (d) => {
                            div.style(
                                'left',
                                this.$d3.event.pageX - div.node().offsetWidth / 2 + 'px'
                            ).style('top', this.$d3.event.pageY - 40 + 'px');
                        })
                        .on('mouseout', (d) => {
                            div.transition().duration(500).style('opacity', 0);
                        });

                    // update legend:
                    legends.style('opacity', (d) => {
                        if (this.filtered.length) {
                            if (this.filtered.indexOf(d) == -1) {
                                return 1;
                            } else {
                                return 0.4;
                            }
                        } else {
                            return 1;
                        }
                    });
                });

            legends
                .append('div')
                .style('width', this.legendRectSize + 'px')
                .style('height', this.legendRectSize + 'px')
                .attr('class', 'legend-color-box')
                .style('background-color', color);

            legends
                .append('p')
                .style('text-transform', 'uppercase')
                .text(function (d) {
                    return d;
                });
        },
        getNextIntegerNumber(v) {
            let iv = Math.floor(v);
            let vs = iv + '';
            let t = vs.length;
            let fn = Number(vs.substring(0, 1));
            let adder = fn > 3 ? 10 ** (t - 1) : 10 ** (t - 2);
            let nv = fn * 10 ** (t - 1) + adder;

            while (nv < v) {
                nv += adder;
            }
            return nv;
        },
        getXRange(xAxis, width) {
            let gap = xAxis.length - 1;
            let mrange = width / (gap == 0 ? 1 : gap);
            return xAxis.map((xa, i) => i * mrange);
        },
        getValue(d) {
            let percent = (d * 100) / this.total;
            return percent;
        },
        getFormattedValue(value) {
            return ('R$' + Math.floor(value * 100) / 100).replace('.', ',');
        },
        redrawChart() {
            this.$d3.select(this.$refs.svgcontent).selectAll('g').remove();
            this.$d3.select(this.$refs.LGbox).selectAll('.legend-box').remove();
            this.generateChart();
        },
    },
};
</script>
